import React from "react";
import classNames from "classnames";
import get from "lodash/get";

//components
import ToggleAddButton from "@/components/ToggleAddButton";
import Avatar from "@/components/Avatar";
import notification from "@/components/notifications";

//helpers
import campaigns from "@/api/query/campaigns";
import useUserProfile from "@/hooks/useUserProfile";
import customDataLayerPush from "@/helpers/customDataLayerPush";

// interfaces
import { ICampaign, ICampaignItem } from "@/types/api/Campaign";
import { ISpaceFullResponse } from "@/types/Space";

//styles
import styles from "@/assets/styles/components/shortlist-campaign-card.module.scss";


interface ShortlistCampaignCardProps {
  campaign: ICampaign;
  space: ISpaceFullResponse;
  wrapperClass?: string;
  onCampaignToggle?: ((isToggled: boolean, data?: ICampaignItem) => void) | null;
  isMobile?: boolean;
  onClick?: () => void;
}

const ShortlistCampaignCard = (props: ShortlistCampaignCardProps) => {
  const { campaign, space, wrapperClass, onCampaignToggle, isMobile, onClick } = props;

  const { data: user } = useUserProfile();

  const { mutateAsync: addSpaceToCampaign } = campaigns.useAddSpaceToCampaign(campaign.id);
  const { mutateAsync: deleteSpaceFromCampaign } = campaigns.useDeleteSpaceFromCampaign({ id: campaign.id });

  const onButtonToggle = async (isToggled: boolean) => {
    if (!isToggled) {
      const resp = await addSpaceToCampaign({ space: space.id })
        .then((res) => {
          customDataLayerPush("space_add_to_campaign", user?.id as number);

          return res;
        })
        .catch(e => {
          const errorMessage = get(e, "response.data[0]", "Something went wrong");

          notification({
            placement: "bottomLeft",
            duration: 5,
            actionText: "Go to campaign",
            actionProps: { href: `/campaign/${campaign.id}/planner` },
            message: "Oops",
            description: errorMessage,
            type: "error",
          });
        });

      resp && onCampaignToggle && onCampaignToggle(isToggled, resp.data);

    } else {
      await deleteSpaceFromCampaign(`${space.id}?modal=true`)
        .catch(e => {
          notification({
            placement: "bottomLeft",
            duration: 5,
            actionText: "Go to campaign",
            actionProps: { href: `/campaign/${campaign.id}/planner` },
            message: "Oops",
            description: "In order to remove this space from your favourites, please first cancel or delete all activities relating to this space on the campaign page.",
            type: "error",
          });

          throw e;
        });
      onCampaignToggle && onCampaignToggle(isToggled);
    }
  };

  const isCampaignShortlisted = campaign?.shortlistedSpaces.some((id: number) => space.id === id);

  return (
    <div
      className={classNames(styles["locl-campaign-card"], wrapperClass)}
      data-testid="locl-campaign-card"
      onClick={() => onClick && onClick()}
    >
      <div className={styles["locl-campaign-card_block"]}>
        <Avatar
          src={get(campaign, "photo.url")}
          fullName={campaign.name}
          className={styles["locl-campaign-card-avatar"]}
          data-testid="locl-campaign-card-avatar"
          size={isMobile ? "small" : "medium"}
        />
        <p className={styles["locl-campaign-card-name"]}>{campaign.name}</p>
      </div>
      {
        onCampaignToggle && (
          <ToggleAddButton
            toggled={!!isCampaignShortlisted}
            onToggle={onButtonToggle}
            data-testid="locl-campaign-card-toggle"
          />
        )
      }
    </div>
  );
};

export default ShortlistCampaignCard;
